import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Card, styled, Tooltip } from '@mui/material';
import { FC, useMemo } from 'react';
import { API } from '~/api';
import { useTr } from '~/text';
import { Row, Text } from '../view.box';

export const StyledCard = styled(Card)(({ theme: { palette } }) => ({
	borderRadius: 24,
	borderWidth: 1,
	borderColor: palette.grey[200],
	borderStyle: 'solid',
	padding: 24,
	display: 'flex',
	flexDirection: 'column',
	gap: 8,
}));

const StyledRow = styled(Row, {
	shouldForwardProp: prop => prop !== 'status',
})<{ status: 'success' | 'error' }>(({ theme, status }) => ({
	height: 24,
	minWidth: 36,
	borderRadius: 24,
	borderStyle: 'solid',
	borderWidth: 1,
	borderColor: theme.palette[status].light,
	backgroundColor: theme.palette[status].light + 10,
}));

export type DashboardCardProps = {
	title:
		| keyof API.AnalyticsByPeriod
		| keyof API.AnalyticsGeneralBase
		| keyof API.AnalyticsGeneralUsers
		| keyof API.AnalyticsUGC;
	currValue: number;
	prevValue?: number;
	percent?: boolean;
	invert?: boolean;
	desc?: string;
};

export const DashboardCard: FC<DashboardCardProps> = ({ title, currValue, desc, prevValue, percent, invert }) => {
	const t = useTr(['dashboardCardTitle']);
	const th = useTr(['dashboardCardHelpers']);

	const meas = useMemo(() => (percent ? '%' : ''), [percent]);
	const diff = prevValue !== undefined ? currValue - prevValue : 0;
	const status = invert ? (diff < 0 ? 'success' : 'error') : diff < 0 ? 'error' : 'success';
	const help = th(title);

	return (
		<StyledCard elevation={0}>
			<Row justifyContent="space-between" alignItems="center" color="text.secondary">
				<Text fontWeight="700" noWrap>
					{t(title)}
				</Text>
				{!!help && (
					<Tooltip title={help} arrow slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}>
						<HelpOutlineIcon color="inherit" fontSize="small" />
					</Tooltip>
				)}
			</Row>
			<Row justifyContent="space-between" alignItems="center">
				<Row alignItems="baseline" gap={1}>
					<Text variant="h4" fontWeight="bold">
						{dashboardCardNumber(currValue)}
						{meas}
					</Text>
					{desc && (
						<Text variant="body2" color="text.secondary" noWrap>
							{desc}
						</Text>
					)}
				</Row>
				{diff !== 0 && (
					<StyledRow gap={0.5} px={1} justifyContent="space-between" alignItems="center" status={status}>
						{diff > 0 ? (
							<ArrowUpwardIcon sx={{ fontSize: 14, color: `${status}.light` }} />
						) : (
							<ArrowDownwardIcon sx={{ fontSize: 14, color: `${status}.light` }} />
						)}
						<Text variant="caption" textAlign="end" color={`${status}.main`} fontWeight="bold">
							{diff}
							{meas}
						</Text>
					</StyledRow>
				)}
			</Row>
		</StyledCard>
	);
};

export function dashboardCardNumber(num: number): string {
	return new Intl.NumberFormat('fr-FR', { notation: 'standard' }).format(num);
}
